import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminReferrers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);

  const [form, setForm] = useState({
    id: null,
    name: "",
    mobile: "",
    notes: "",
    is_active: 1,
  });

  const isEditing = form.id != null;

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/admin/referrers", {
        params: {
          q: opts.search !== undefined ? opts.search : search || undefined,
          include_inactive: includeInactive ? "1" : undefined,
        },
      });
      setItems(data);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load referrers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      id: null,
      name: "",
      mobile: "",
      notes: "",
      is_active: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }

    try {
      setLoading(true);
      if (isEditing) {
        await api.put(`/api/admin/referrers/${form.id}`, {
          name: form.name.trim(),
          mobile: form.mobile || null,
          notes: form.notes || null,
          is_active: form.is_active ? 1 : 0,
        });
      } else {
        await api.post("/api/admin/referrers", {
          name: form.name.trim(),
          mobile: form.mobile || null,
          notes: form.notes || null,
          is_active: form.is_active ? 1 : 0,
        });
      }
      resetForm();
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save referrer");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || "",
      mobile: item.mobile || "",
      notes: item.notes || "",
      is_active: item.is_active ? 1 : 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Mark this referrer as inactive?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/admin/referrers/${id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete referrer");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f8fafc" },
    container: {
      maxWidth: 960,
      margin: "0 auto",
      padding: 16,
    },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    textarea: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
      minHeight: 60,
    },
    buttonPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    buttonGhost: {
      background: "#e2e8f0",
      color: "#0f172a",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      fontSize: 12,
      border: `1px solid ${active ? "#0f766e" : "#dc2626"}`,
      color: active ? "#0f766e" : "#dc2626",
      background: active ? "#E7F8F1" : "#FDECEC",
    }),
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Referrer Master</h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Form Card */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={styles.label}>Name *</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Referred by name"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Mobile (optional)</label>
                <input
                  style={styles.input}
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="Mobile number"
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Notes (optional)</label>
              <textarea
                style={styles.textarea}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                id="ref-active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
                }
              />
              <label htmlFor="ref-active" style={{ fontSize: 14 }}>
                Active
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button type="submit" style={styles.buttonPrimary} disabled={loading}>
                {isEditing ? "Update Referrer" : "Add Referrer"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Filter + List */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input
              style={{ ...styles.input, maxWidth: 260 }}
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              style={styles.buttonGhost}
              onClick={() => loadData({ search })}
            >
              Search
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  // reload list when toggle changes
                  setTimeout(() => loadData({}), 0);
                }}
              />
              Show inactive
            </label>
          </div>

          {loading && <div>Loading...</div>}
          {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}

          {!loading && !items.length && <div>No referrers found.</div>}

          {!!items.length && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: 8, textAlign: "left" }}>Name</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Mobile</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Notes</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 8 }}>{item.name}</td>
                      <td style={{ padding: 8 }}>{item.mobile || "-"}</td>
                      <td style={{ padding: 8 }}>{item.notes || "-"}</td>
                      <td style={{ padding: 8 }}>
                        <span style={styles.pill(item.is_active)}>
                          {item.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: 8, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          style={{ ...styles.buttonGhost, padding: "4px 10px", marginRight: 6 }}
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.buttonGhost, padding: "4px 10px", background: "#fee2e2", border: "1px solid #fecaca" }}
                          onClick={() => handleDelete(item.id)}
                          disabled={!item.is_active}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
